/* eslint-disable no-param-reassign */
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
    chain, cloneDeep, countBy, escapeRegExp, findIndex, get, includes, isArray, isEmpty, last, map,
} from 'lodash';
import { formatDate, subtractDaysAndFormat } from '../../../utils/dateUtils';

import {
    PositionAtsPushStatus,
    positionEventFlowTypes,
    positionEvents,
    PositionStatuses,
} from '../../../constants/Position';
import { CANDIDATE_FEEDBACK_STATUSES } from '../../../api/CandidateProfileHttpService';
import getIsPositionRequiresCalibration from '../../../utils/getIsPositionRequiresCalibration';
import EMPLOYER_WORKFLOW_STATUSES from '../../../constants/EmployerWorkflowStatus';
import { darkslategray } from '../../../constants/Colors';
import AuthorizationContext from '../../../contexts/AuthorizationContext';
import { getIsSwitchedOn, getIsAutoCampaignEverStarted } from '../../../contexts/AutoCampaignContext';
import EmployerContext from '../../../contexts/EmployerContext';
import { EmployerRepAuthTypes } from '../../../constants/employerRepaAuthTypes';

const OPEN_POSITION_HANDLER = 'OPEN_POSITION';

export const POSITIONS_LIST_TOOLTIPS_DELAY_IN_MS = 400;

export const SORT_ORDER = {
    DESC: 'desc',
    ASC: 'asc',
};

export const POSITION_COLUMN_ID = {
    TITLE: 'jobTitle',
    STATUS: 'feedbackStatus',
    POSTED: 'posted',
    DIVERSITY_LAST_APPLY_DATE: 'diversityLastApplyDate',
    ACTIONS: 'actions',
    EMPLOYER_REP: 'employerRep',
    EMPLOYER_ATS_RECRUITER: 'employerAtsRecruiter',
    ATS_DEPARTMENT: 'employerDepartment',
    COMPANY: 'company',
};

const flexParams = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const getTableColumns = (
    positionEmployerRepFilter,
    positionEmployerAtsRecruiterFilter,
    departmentFilterValue,
    positionCompanyFilter,
) => ([
    {
        id: POSITION_COLUMN_ID.TITLE,
        style: {
            color: darkslategray,
            padding: '10px',
            workBreak: 'break-all',
            height: '40px',
            overflow: 'hidden',
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'NAME',
        testId: 'jt-col-name',
        useSort: true,
    },
    positionEmployerRepFilter === '' ? {
        id: POSITION_COLUMN_ID.EMPLOYER_REP,
        style: {
            color: darkslategray,
            padding: '10px',
            workBreak: 'break-all',
            height: '40px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'EMPLOYER_JOB_LIST_RECRUITER_COLUMN',
        testId: 'jt-col-employer_rep',
        useSort: true,
    } : null,
    positionCompanyFilter === 'ALL' ? {
        id: POSITION_COLUMN_ID.COMPANY,
        style: {
            color: darkslategray,
            padding: '10px',
            workBreak: 'break-all',
            height: '40px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'EMPLOYER_JOB_LIST_COMPANY_COLUMN',
        testId: 'jt-col-company',
        useSort: true,
    } : null,
    departmentFilterValue === 'ALL' ? {
        id: POSITION_COLUMN_ID.ATS_DEPARTMENT,
        style: {
            color: darkslategray,
            padding: '10px',
            workBreak: 'break-all',
            height: '40px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            position: 'relative',
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'EMPLOYER_JOB_LIST_ATS_DEPARTMENT_COLUMN',
        testId: 'jt-col-employer_ats_department',
        useSort: true,
    } : null,
    positionEmployerAtsRecruiterFilter === 'ALL' ? {
        id: POSITION_COLUMN_ID.EMPLOYER_ATS_RECRUITER,
        style: {
            color: darkslategray,
            padding: '10px',
            workBreak: 'break-all',
            height: '40px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'EMPLOYER_JOB_LIST_ATS_RECRUITER_COLUMN',
        testId: 'jt-col-employer_ats_recruiter',
        useSort: true,
    } : null,
    {
        id: POSITION_COLUMN_ID.STATUS,
        style: {
            color: darkslategray,
            height: '40px',
            width: '100%',
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'EMPLOYER_POSITIONS_TABLE_COLUMN_STATUS_LABEL',
        testId: 'jt-col-action',
        align: 'center',
        useSort: true,
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.FEEDBACK,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'RATE_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_RATED_TOOLTIP',
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.CONTACT,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'QUALIFIED_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_CONTACT_TOOLTIP',
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.CONTACTED,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'CONTACTED_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_CONTACT_TOOLTIP',
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.REVIEWED,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'CLAIMED_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_INTERESTED_TOOLTIP',
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.INTERVIEW_AND_ASSESSMENT,
        style: {
            color: darkslategray,
            padding: '8px',
            // minWidth: '45px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'INTERVIEW_AND_ASSESSMENT_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_INTERVIEW_TOOLTIP',
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.HIRED,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'HIRED_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_HIRED_TOOLTIP',
    },
    {
        id: EMPLOYER_WORKFLOW_STATUSES.REJECTED,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            ...flexParams,
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
        label: 'REJECTED_EMPLOYER',
        align: 'center',
        title: 'EMPLOYER_JOBS_LIST_NONE_NOT_FIT_TOOLTIP',
    },
    {
        id: POSITION_COLUMN_ID.POSTED,
        label: 'POSTED',
        align: 'center',
        useSort: true,
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px 0',
            fontSize: '0.9rem',
            height: '54px',
            textAlign: 'center',
            flexGrow: 1,
        },
        headerStyle: {
            flexGrow: 1,
        },
    },
    {
        id: POSITION_COLUMN_ID.DIVERSITY_LAST_APPLY_DATE,
        label: 'EMPLOYER_JOB_LIST_DIVERSITY_AI_COLUMN',
        align: 'center',
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
            flexGrow: 1,
        },
        cellStyle: {
            color: darkslategray,
            padding: '8px',
            fontSize: '0.9rem',
            height: '54px',
            flexGrow: 1,
        },
        testId: 'jt-col-diversity-last-apply',
        headerStyle: {
            flexGrow: 1,
        },
    },
    {
        id: POSITION_COLUMN_ID.ACTIONS,
        label: 'EMPLOYER_POSITIONS_TABLE_COLUMN_ACTIONS_LABEL',
        align: 'center',
        style: {
            color: darkslategray,
            padding: '8px',
            height: '40px',
        },
        headerStyle: {
            flexGrow: 1,
        },
    },
].filter((column) => column));

export const FilterDatePostedOptionValues = {
    ALL: 'ALL',
    LAST_WEEK: 'LAST_WEEK',
    TWO_WEEKS_AGO: '2_WEEKS_AGO',
    MONTH: '30_DAYS',
    TWO_MONTHS: '60_DAYS',
    FOUR_MONTHS: '120_DAYS',
};

export const filterDatePostedOptions = [
    {
        value: FilterDatePostedOptionValues.ALL,
        label: 'ALL',
        daysBackCount: null,
    },
    {
        value: FilterDatePostedOptionValues.LAST_WEEK,
        label: 'WEEK',
        daysBackCount: 7,
    },
    {
        value: FilterDatePostedOptionValues.TWO_WEEKS_AGO,
        label: 'TWO_WEEKS',
        daysBackCount: 14,
    },
    {
        value: FilterDatePostedOptionValues.MONTH,
        label: '30_DAYS',
        daysBackCount: 30,
    },
    {
        value: FilterDatePostedOptionValues.TWO_MONTHS,
        label: '60_DAYS',
        daysBackCount: 60,
    },
    {
        value: FilterDatePostedOptionValues.FOUR_MONTHS,
        label: '120_DAYS',
        daysBackCount: 120,
    },
];

export const getEarliestCreationDate = (positionPostedDateFilterValue) => {
    if (!positionPostedDateFilterValue || positionPostedDateFilterValue === FilterDatePostedOptionValues.ALL) {
        return null;
    }

    const daysBackCount = chain(filterDatePostedOptions)
        .find({ value: positionPostedDateFilterValue })
        .get('daysBackCount')
        .value();

    const earliestCreationDate = subtractDaysAndFormat(Date.now(), daysBackCount, 'YYYY-MM-DD');

    return earliestCreationDate;
};

export const PositionStatusFilter = {
    ALL: 'ALL',
    COMPLETE_JOB_DEFINITION: 'COMPLETE_JOB_DEFINITION',
    VIEW_CANDIDATES: 'VIEW_CANDIDATES',
    AUTO_CAMPAIGN_ON: 'AUTO_CAMPAIGN_ON',
    AUTO_CAMPAIGN_OFF: 'AUTO_CAMPAIGN_OFF',
    WAITING_FOR_REQUEST: 'WAITING_FOR_REQUEST',
};

export const getFilterPositionStatusOptions = (isManualQualifyEnabled) => ([
    { value: PositionStatusFilter.ALL, label: 'ALL' },
    { value: PositionStatusFilter.COMPLETE_JOB_DEFINITION, label: 'COMPLETE_JOB_BUTTON' },
    { value: PositionStatusFilter.VIEW_CANDIDATES, label: isManualQualifyEnabled ? 'SELECTION_PAGE_LABEL' : 'VIEW_CANDIDATES' },
    { value: PositionStatusFilter.AUTO_CAMPAIGN_ON, label: 'AUTO_CAMPAIGN_ON' },
    { value: PositionStatusFilter.AUTO_CAMPAIGN_OFF, label: 'AUTO_CAMPAIGN_OFF' },
    { value: PositionStatusFilter.WAITING_FOR_REQUEST, label: 'WAITING_FOR_REQUEST' },
]);

export const OpenClosedStatusFilter = {
    ALL: 'ALL',
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
    CLOSED_BY_ATS: 'CLOSED_BY_ATS',
};

export const filterOpenClosedStatusOptions = [
    { value: OpenClosedStatusFilter.ALL, label: 'ALL' },
    { value: OpenClosedStatusFilter.OPEN, label: 'OPEN' },
    { value: OpenClosedStatusFilter.CLOSED, label: 'CLOSED' },
    { value: OpenClosedStatusFilter.CLOSED_BY_ATS, label: 'CLOSED_BY_ATS' },
];

export const POSITIONS_FILTER_COMMON_OPTIONS = [
    { value: 'ALL', label: 'ALL' },
];

export const POSITIONS_FILTER_RECRUITER_COMMON_OPTIONS = [
    { value: '', label: 'ALL' },
];

export const NO_VALUE = '---';

export const MISSING_DEPARTMENT_FILTER_OPTION = [
    { value: NO_VALUE, label: 'MISSING_DEPARTMENT_FILTER_OPTION_LABEL' },
];

export const filterEmployerJobsByTitle = (jobTitle, selectedFilterValue) => {
    const regexEscaped = escapeRegExp(selectedFilterValue);
    const regExp = new RegExp(regexEscaped, 'i');
    return isEmpty(selectedFilterValue) || jobTitle.match(regExp);
};

export const filterEmployerJobsByAtsJobId = (position, selectedFilterValue) => {
    const atsJobId = position?.atsSettings?.atsJobId;
    if (atsJobId) {
        const regexEscaped = escapeRegExp(selectedFilterValue);
        const regExp = new RegExp(regexEscaped, 'i');
        return isEmpty(selectedFilterValue) || atsJobId.match(regExp);
    }
    return false;
};

export const filterEmployerJobsByCompany = (position, selectedFilterValue, resellerEmployersList) => {
    const { employerId } = position;
    const regexEscaped = escapeRegExp(selectedFilterValue);
    const regExp = new RegExp(regexEscaped, 'i');
    const findResult = findIndex(resellerEmployersList, ({ name, _id }) => employerId === _id && name.match(regExp));
    return findResult >= 0;
};

export const filterEmployerJobsByAtsCompanyId = (position, selectedFilterValue, resellerEmployersList) => {
    const { employerId } = position;
    const regexEscaped = escapeRegExp(selectedFilterValue);
    const regExp = new RegExp(regexEscaped, 'i');
    const findResult = findIndex(resellerEmployersList, ({ resellerCompanyId, _id }) => employerId === _id && resellerCompanyId.match(regExp));
    return findResult >= 0;
};

export const getFirstActiveJobDate = (status) => {
    if (!status) {
        return null;
    }

    let firstActiveDate = status.timestamp;
    const history = status.history || [];
    for (let i = 0; i < history.length; i++) {
        if (history[i].status === 'Active') {
            firstActiveDate = history[i].timestamp;
        }
    }
    return firstActiveDate;
};

export const getLocationString = (location) => {
    const {
        city, region, state, country,
    } = location;

    return [city, region, state, country]
        .filter(Boolean)
        .join(', ');
};

export const getLocationData = (position) => {
    const locations = get(position, 'locations', []);
    const additionalLocations = locations.splice(1);

    const main = getLocationString(locations[0]);
    const additional = additionalLocations.length
        ? additionalLocations.map((location) => getLocationString(location)).join('; ') : '';
    const additionalTemplate = additionalLocations.length
        ? additionalLocations.map((location) => `<div>${getLocationString(location)}</div>`).join('') : '';
    const additionalCount = additionalLocations.length;

    return {
        main,
        additional,
        additionalCount,
        additionalTemplate: `<span>${additionalTemplate}</span>`,
    };
};

const checkIfRequestFeedbacksPositions = (position) => {
    if (position.isCompleteJobDefinitionAvailable) {
        return true;
    }

    return ![
        PositionStatuses.DONE,
        PositionStatuses.CLOSED_BY_ATS,
        PositionStatuses.CANCELLED,
        PositionStatuses.FINISHED,
    ].includes(position.status.current);
};

export const getRequestFeedback = (positions, t) => {
    const positionsWithFeedbackData = map(positions, (originalPosition) => {
        const position = cloneDeep(originalPosition);
        const isRequestFeedbacksPosition = checkIfRequestFeedbacksPositions(position);
        if (!isRequestFeedbacksPosition) {
            position.onLinkClickHandler = OPEN_POSITION_HANDLER;
        } else {
            const { feedbackOnCandidates, generatedFeedbackTalents } = position;
            const hasGeneratedTalents = generatedFeedbackTalents ? generatedFeedbackTalents.length > 0 : false;
            const feedbackPendingCount = (countBy(
                feedbackOnCandidates,
                (feedback) => feedback.status === CANDIDATE_FEEDBACK_STATUSES.PENDING,
            ).true || 0);
            const feedbackAcceptedCount = (countBy(
                feedbackOnCandidates,
                (feedback) => feedback.status === CANDIDATE_FEEDBACK_STATUSES.ACCEPTED,
            ).true || 0);
            const isCompleteJobDefinitionAvailable = getIsPositionRequiresCalibration(position);
            const feedbackEvents = position.events.filter((event) => event.flowType === positionEventFlowTypes.feedback);
            const isFeedbackCompleted = get(last(feedbackEvents), 'eventType') === positionEvents.feedbackRatingCompleted;
            const isFeedbackRequested = get(last(feedbackEvents), 'eventType') === positionEvents.feedbackRequested;
            const isFeedbackInProgress = get(last(feedbackEvents), 'eventType') === positionEvents.feedbackRatingUpdated;
            const isAutoCampaignSwitchedOn = getIsSwitchedOn(position.autoCampaign);
            const isAutoCampaignEverStarted = getIsAutoCampaignEverStarted(position.autoCampaign);
            // auto campaign (ai sourcing) status is off if it was ever started
            const isAutoCampaignSwitchedOff = !isAutoCampaignSwitchedOn && isAutoCampaignEverStarted;
            switch (true) {
                case isCompleteJobDefinitionAvailable:
                    position.translatedStatus = t('COMPLETE_JOB_BUTTON');
                    break;
                case position.status.current === 'Ready':
                    position.translatedStatus = t('READY');
                    break;
                case position.status.current === 'Draft':
                    position.translatedStatus = t('POSITION_STATUS_DRAFT');
                    break;
                case isFeedbackRequested || isFeedbackInProgress || hasGeneratedTalents:
                    position.feedbackStatus = 'VIEW_CANDIDATES';
                    position.translatedStatus = t(position.feedbackStatus);
                    position.translatedStatus = isAutoCampaignSwitchedOn ? t('AUTO_CAMPAIGN_ON') : position.translatedStatus;
                    position.translatedStatus = isAutoCampaignSwitchedOff ? t('AUTO_CAMPAIGN_OFF') : position.translatedStatus;
                    break;
                case isFeedbackCompleted:
                    position.feedbackStatus = 'ENGAGING_WITH_TALENTS_FOR_YOU';
                    position.translatedStatus = t(position.feedbackStatus);
                    break;
                case feedbackAcceptedCount > 0 && feedbackPendingCount > 0:
                    position.feedbackStatus = 'PROVIDE_FEEDBACK_ON_PROFILE';
                    position.translatedStatus = t(position.feedbackStatus);
                    break;
                case feedbackAcceptedCount > 0 && feedbackPendingCount === 0:
                    position.feedbackStatus = 'THANKS_FOR_RATING_OUR_TE_WILL_PROVIDE_PROSPECTS';
                    position.translatedStatus = t(position.feedbackStatus);
                    break;
                case feedbackPendingCount > 0 && feedbackAcceptedCount === 0:
                    position.feedbackStatus = 'PROVIDE_FEEDBACK_ON_PROFILE';
                    position.translatedStatus = t(position.feedbackStatus);
                    break;
                default:
                    position.feedbackStatus = 'BOT_WILL_PROVIDE_PROSPECTS';
                    position.translatedStatus = t(position.feedbackStatus);
                    break;
            }

            position.feedbackPendingCount = feedbackPendingCount;
            position.feedbackAcceptedCount = feedbackAcceptedCount;
            position.feedbackAllCount = feedbackAcceptedCount + feedbackPendingCount;
            position.isCompleteJobDefinitionAvailable = isCompleteJobDefinitionAvailable;
        }
        return position;
    });

    return positionsWithFeedbackData;
};

export const filterEmployerJobsByOpenCloseState = (position, selectedFilterValue) => {
    if (selectedFilterValue === 'OPEN') {
        return [
            PositionStatuses.DRAFT,
            PositionStatuses.READY,
            PositionStatuses.ACTIVE,
        ].includes(position.status.current);
    }

    if (selectedFilterValue === 'CLOSED') {
        return [PositionStatuses.CANCELLED, PositionStatuses.FINISHED, PositionStatuses.CLOSED_BY_ATS, ''].includes(position.status.current);
    }

    if (selectedFilterValue === 'CLOSED_BY_ATS') {
        return PositionStatuses.CLOSED_BY_ATS === position.status.current;
    }

    if (selectedFilterValue === 'ALL') {
        return true;
    }

    return false;
};

export const getStatusPayload = (selectedFilterValue) => {
    switch (selectedFilterValue) {
        case 'OPEN':
            return {
                status: [
                    PositionStatuses.DRAFT,
                    PositionStatuses.READY,
                    PositionStatuses.ACTIVE,
                ],
            };
        case 'CLOSED':
            return {
                status: [
                    PositionStatuses.CANCELLED,
                    PositionStatuses.FINISHED,
                    PositionStatuses.CLOSED_BY_ATS,
                    '',
                ],
            };
        case 'CLOSED_BY_ATS':
            return {
                status: [
                    PositionStatuses.CLOSED_BY_ATS,
                ],
            };
        default:
            return {};
    }
};

export const qualifyEmployerJobByOpenCloseState = (position) => {
    if ([PositionStatuses.ACTIVE, PositionStatuses.DRAFT, PositionStatuses.READY].includes(position.status.current)) {
        return 'OPEN';
    }

    if ([PositionStatuses.CANCELLED, PositionStatuses.FINISHED].includes(position.status.current)) {
        return 'CLOSED';
    }

    return null;
};

export const filterEmployerJobsByStatus = (position, selectedFilterValue) => {
    const isAutoCampaignSwitchedOn = getIsSwitchedOn(position.autoCampaign);
    const isAutoCampaignEverStarted = getIsAutoCampaignEverStarted(position.autoCampaign);
    // auto campaign (ai sourcing) status is off if it was ever started
    const isAutoCampaignSwitchedOff = !isAutoCampaignSwitchedOn && isAutoCampaignEverStarted;
    const isAutoCampaignStatus = isAutoCampaignSwitchedOn || isAutoCampaignEverStarted;

    if (selectedFilterValue === PositionStatusFilter.ALL) {
        return true;
    }

    if (selectedFilterValue === PositionStatusFilter.COMPLETE_JOB_DEFINITION) {
        return !!position.isCompleteJobDefinitionAvailable && !position.isRequestFeedbackSent;
    }

    if (selectedFilterValue === PositionStatusFilter.WAITING_FOR_REQUEST) {
        return !!position.isCompleteJobDefinitionAvailable && position.isRequestFeedbackSent;
    }

    if (selectedFilterValue === PositionStatusFilter.VIEW_CANDIDATES) {
        return !isAutoCampaignStatus && position.feedbackStatus === 'VIEW_CANDIDATES';
    }

    if (selectedFilterValue === PositionStatusFilter.AUTO_CAMPAIGN_ON) {
        return isAutoCampaignSwitchedOn;
    }

    if (selectedFilterValue === PositionStatusFilter.AUTO_CAMPAIGN_OFF) {
        return isAutoCampaignSwitchedOff;
    }

    return false;
};

export const filterEmployerJobsByEmployerRepId = (position, filterValue, options) => {
    if (filterValue === 'ALL' || filterValue === '') {
        return true;
    }
    if (isArray(options)) {
        const filteredEmployerRepIds = chain(options)
            .filter((item) => item.sortKey && item.sortKey.indexOf(filterValue) >= 0)
            .map('value')
            .value();
        if (!filteredEmployerRepIds.length) {
            return true;
        }
        return includes(filteredEmployerRepIds, position.employerRepId);
    }
    return position.employerRepId === filterValue;
};

export const filterEmployerJobsByEmployerAtsRecruiter = (position, filterValue) => {
    if (filterValue === 'ALL') {
        return true;
    }
    const atsRecruiterEmail = position?.atsSettings?.atsRecruiter?.email;
    if (atsRecruiterEmail) {
        return atsRecruiterEmail === filterValue;
    }
    return false;
};

export const filterEmployerJobsByAtsDepartment = (position, filterValue, isDepartmentsEnabled) => {
    if (!isDepartmentsEnabled) {
        return true;
    }

    if (filterValue === 'ALL') {
        return true;
    }

    const atsDepartmentId = position?.employerDepartment?.id;

    if (filterValue === NO_VALUE) {
        return !atsDepartmentId;
    }

    if (atsDepartmentId) {
        return atsDepartmentId === filterValue;
    }

    return false;
};

export const getDateInfo = (input) => {
    const nowDate = new Date();
    const date = new Date(input);
    if (date.toString() === 'Invalid Date') {
        return {
            input,
        };
    }

    const dateString = formatDate(new Date(input), 'MM/DD/YY');
    let delta = (nowDate - date) / 1000;

    if (delta < 60) {
        return {
            label: 'JUST_NOW',
        };
    }
    // calculate (and subtract) Year
    const years = Math.floor(delta / 31536000);
    delta -= years * 31536000;

    if (years) {
        return {
            dateString,
            description: `(${years} year${years === 1 ? '' : 's'} ago)`,
        };
    }
    // calculate (and subtract) Month
    const months = Math.floor(delta / 2592000);
    delta -= months * 2592000;

    if (months) {
        return {
            dateString,
            description: `(${months} month${months === 1 ? '' : 's'} ago)`,
        };
    }

    // calculate (and subtract) whole days
    const days = Math.floor(delta / 86400);
    delta -= days * 86400;

    if (days) {
        return {
            dateString,
            description: `(${days} day${days === 1 ? '' : 's'} ago)`,
        };
    }

    // calculate (and subtract) whole hours
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;

    if (hours) {
        return {
            dateString,
            description: `(${hours} hour${hours === 1 ? '' : 's'} ago)`,
        };
    }

    // calculate (and subtract) whole minutes
    const minutes = Math.floor(delta / 60) % 60;

    if (minutes === 0) {
        return {
            label: 'JUST_NOW',
        };
    }

    return {
        dateString,
        description: `(${minutes} minute${minutes === 1 ? '' : 's'} ago)`,
    };
};

export const getWorkflowCounter = (input, name) => {
    if (!input) {
        return 0;
    }
    for (let i = 0; i < input.length; i++) {
        if (input[i].status === name) {
            return input[i].count;
        }
    }
    return 0;
};

export const getWorkflowCounterV2 = (input, name) => {
    if (!input) {
        return 0;
    }
    for (let i = 0; i < input.length; i++) {
        if (input[i].status === name) {
            return input[i];
        }
    }
    return 0;
};

// Modified by AI on 05/13/2026. Edit #1.
export const useIsRedirectNeededCheck = () => {
    const { employerId: employerIdInUrl } = useParams();

    const {
        isTalenyaUser,
        isTalentExpertUser,
        employerRep,
        isEmployerRepAdmin,
    } = useContext(AuthorizationContext);

    const { employer, isResellerPartner } = useContext(EmployerContext);
    const employerIdInEmployerRep = employerRep?.employerId;

    const hasRightToSeeAllPositions = isTalenyaUser || isTalentExpertUser;
    const isDataLoaded = hasRightToSeeAllPositions || (employerIdInEmployerRep && !isEmpty(employer));

    const canEmployerRepGetAssociatedEmployersJobs = isResellerPartner && isEmployerRepAdmin;

    const employerAuthType = employer?.settings?.authData?.authType;

    const isSsoAuthEmployer = employerAuthType === EmployerRepAuthTypes.SSO;

    const isMultiUser = !!employerRep?.multiUser;

    // when auth type is sso
    // we try to relogin if current user not from current employer
    const isNeedToSupportMultiSsoUsers = isSsoAuthEmployer;

    if (hasRightToSeeAllPositions || !isDataLoaded || isNeedToSupportMultiSsoUsers || isMultiUser) {
        return { isRedirectToCorrectEmployersJobsListNeeded: false };
    }

    const canEmployerRepSeePositions = (employerIdInEmployerRep === employerIdInUrl)
        || (employerIdInEmployerRep === employer?.resellerEmployerId)
        || (canEmployerRepGetAssociatedEmployersJobs && employerIdInEmployerRep === employer?._id);

    const isRedirectToCorrectEmployersJobsListNeeded = !canEmployerRepSeePositions && !hasRightToSeeAllPositions;

    return { isRedirectToCorrectEmployersJobsListNeeded };
};

export const checkIfWaitingForFeedbackLabelIsShown = ({ employer, position }) => {
    const isPushStatusEnabled = get(employer, 'settings.isAtsJobsAutoPushStatusEnabled');
    const positionEventsPssStatuses = map(position?.events, 'content.data.status.pssStatus');
    const isCalibrationReadyStatusExist = includes(positionEventsPssStatuses, PositionAtsPushStatus.calibration_ready);
    const shouldShowWaitingForFeedbackLabel = (isCalibrationReadyStatusExist && isPushStatusEnabled) || position?.isRequestFeedbackSent;
    return shouldShowWaitingForFeedbackLabel;
};
